import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../redux/axios";

const initialState = {
  isFetching: false,
  isSuccess: false,
  isError: false,
  errorMessage: "",
  successMessage: "",
};

/**
 * Regenerate / Update API key thunk.
 * PUT /login/updateApiKey
 * Sends encryptData as application/x-www-form-urlencoded with Authorization header.
 */
export const regenerateApiKey = createAsyncThunk(
  "apiKey/regenerate",
  async (encryptData, thunkAPI) => {
    try {
      const endpoint = "/login/updateApiKey";
      const body = `encryptData=${encryptData}`;

      const response = await axios.put(endpoint, body, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
      });

      if (response.status?.toString().startsWith("2")) {
        return { data: response.data, status: response.status };
      } else {
        return thunkAPI.rejectWithValue(
          response.data?.message || "Failed to regenerate API key"
        );
      }
    } catch (err) {
      console.error("Regenerate API Key Error:", err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to regenerate API key. Please try again.";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateApiKeySlice = createSlice({
  name: "updateApiKey",
  initialState,
  reducers: {
    clearApiKeyStatus: (state) => {
      state.isFetching = false;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = "";
      state.successMessage = "";
    },
    setApiKeySuccess: (state, action) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.isError = false;
      state.successMessage = action.payload || "";
    },
    setApiKeyError: (state, action) => {
      state.isFetching = false;
      state.isSuccess = false;
      state.isError = true;
      state.errorMessage = action.payload || "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(regenerateApiKey.pending, (state) => {
        state.isFetching = true;
        state.isSuccess = false;
        state.isError = false;
        state.errorMessage = "";
        state.successMessage = "";
      })
      .addCase(regenerateApiKey.fulfilled, (state, action) => {
        state.isFetching = false;
        state.isSuccess = true;
        state.isError = false;
        state.successMessage =
          action.payload?.data?.message ||
          "API key reset successfully";
      })
      .addCase(regenerateApiKey.rejected, (state, action) => {
        state.isFetching = false;
        state.isSuccess = false;
        state.isError = true;
        state.errorMessage =
          action.payload || "Failed to regenerate API key. Please try again.";
      });
  },
});

export const { clearApiKeyStatus, setApiKeySuccess, setApiKeyError } =
  updateApiKeySlice.actions;

export const updateApiKeySelector = (state) =>
  state.updateApiKey || initialState;

export default updateApiKeySlice.reducer;
